import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";

import { api, ApiError, type Report, type ReportTally } from "../api";
import { DateRange, downloadCsv, presets, type Range } from "../DateRange";
import { reasonLabel, taka } from "../format";

// Totals for the orders placed in a date range: what happened to them, the money,
// how fast pharmacies answered, and the same per pharmacy, rider and day.

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : "—");
const minutes = (m: number | null) => (m === null ? "—" : `${m} min`);

export default function OpsReports() {
  const [range, setRange] = useState<Range>(presets()[0]!.range);
  const report = useQuery({ queryKey: ["ops-report", range], queryFn: () => api<Report>(`/ops/reports?from=${range.from}&to=${range.to}`), refetchInterval: 60_000 });
  const r = report.data;

  const csv = () => {
    if (!r) return;
    const head = ["group", "name", "placed", "delivered", "rejected", "cancelled", "open", "delivered_value_taka", "delivery_fees_taka", "cash_collected_taka", "failed_deliveries", "median_answer_min", "answers_over_10_min"];
    const row = (group: string, name: string, t: ReportTally) => [group, name, t.placed, t.delivered, t.rejected, t.cancelled, t.open, t.deliveredPaisa / 100, t.feesPaisa / 100, t.cashPaisa / 100, t.failedDeliveries, t.medianAnswerMinutes, t.lateAnswers];
    downloadCsv(`savon-report-${r.from}-to-${r.to}.csv`, [
      head,
      row("total", `${r.from} to ${r.to}`, r.totals),
      ...r.byDay.map((d) => row("day", d.day, d)),
      ...r.byPharmacy.map((p) => row("pharmacy", p.name, p)),
      ...r.byRider.map((x) => row("rider", x.name, x)),
    ]);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold">Reports</h1>
        <Button size="sm" variant="outline" className="ml-auto" disabled={!r || r.totals.placed === 0} onClick={csv}>
          Download CSV
        </Button>
      </div>
      <DateRange value={range} onChange={setRange} />
      <p className="text-xs text-muted-foreground">Counts orders placed in these dates (Dhaka time), whatever day they were delivered. Deposits count by the day they were recorded.</p>

      {report.isLoading && <p className="text-muted-foreground">Loading…</p>}
      {report.error && <p className="text-destructive">{report.error instanceof ApiError && report.error.code === "range_too_long" ? "Pick at most 93 days." : "Could not load the report."}</p>}

      {r && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Orders placed" value={String(r.totals.placed)} />
            <Stat label="Delivered" value={String(r.totals.delivered)} sub={pct(r.totals.delivered, r.totals.placed)} />
            <Stat label="Rejected" value={String(r.totals.rejected)} sub={pct(r.totals.rejected, r.totals.placed)} />
            <Stat label="Cancelled" value={String(r.totals.cancelled)} sub={pct(r.totals.cancelled, r.totals.placed)} />
            <Stat label="Delivered value" value={taka(r.totals.deliveredPaisa)} sub={`fees ${taka(r.totals.feesPaisa)}`} />
            <Stat label="Cash collected" value={taka(r.totals.cashPaisa)} sub={`deposits ${taka(r.totals.depositsPaisa)} (${r.totals.deposits})`} />
            <Stat label="Pharmacy answer" value={minutes(r.totals.medianAnswerMinutes)} sub={`median · ${r.totals.lateAnswers} over 10 min`} />
            <Stat label="Failed deliveries" value={String(r.totals.failedDeliveries)} sub={`${r.totals.open} orders still open`} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Reasons title="Why orders were rejected" reasons={r.rejectReasons} />
            <Reasons title="Why orders were cancelled" reasons={r.cancelReasons} />
          </div>

          <Table title="By pharmacy" rows={r.byPharmacy.map((p) => ({ key: String(p.pharmacyId), name: p.name, t: p }))} />
          <Table title="By rider" rows={r.byRider.map((x) => ({ key: String(x.riderId), name: x.name, t: x }))} empty="No orders went out on a run." />
          {r.byDay.length > 1 && <Table title="By day" rows={r.byDay.map((d) => ({ key: d.day, name: d.day, t: d }))} />}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function Reasons({ title, reasons }: { title: string; reasons: Record<string, number> }) {
  const list = Object.entries(reasons).sort((a, b) => b[1] - a[1]);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {list.length === 0 && <p className="text-muted-foreground">None.</p>}
        {list.map(([code, n]) => (
          <div key={code} className="flex justify-between gap-3">
            <span>{reasonLabel(code)}</span>
            <span className="tabular-nums">{n}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Table({ title, rows, empty }: { title: string; rows: { key: string; name: string; t: ReportTally }[]; empty?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{empty ?? "Nothing in these dates."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm tabular-nums">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="p-2">Name</th>
                  <th className="p-2">Placed</th>
                  <th className="p-2">Delivered</th>
                  <th className="p-2">Rejected</th>
                  <th className="p-2">Cancelled</th>
                  <th className="p-2">Value</th>
                  <th className="p-2">Cash</th>
                  <th className="p-2">Answer (median)</th>
                  <th className="p-2">Failed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ key, name, t }) => (
                  <tr key={key} className="border-t">
                    <td className="p-2 font-medium">{name}</td>
                    <td className="p-2">{t.placed}</td>
                    <td className="p-2">{t.delivered}</td>
                    <td className="p-2">{t.rejected}</td>
                    <td className="p-2">{t.cancelled}</td>
                    <td className="p-2">{taka(t.deliveredPaisa)}</td>
                    <td className="p-2">{taka(t.cashPaisa)}</td>
                    <td className="p-2">{minutes(t.medianAnswerMinutes)}</td>
                    <td className="p-2">{t.failedDeliveries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
